import React, { useRef, useEffect, useState } from 'react';
import type { ChatMessage } from '../../types';
import { ChatMessageItem } from './ChatMessageItem';

interface ChatMessageListProps {
  messages: ChatMessage[];
  isStreaming?: boolean;
  streamingContent?: string;
}

export const ChatMessageList: React.FC<ChatMessageListProps> = ({
  messages,
  isStreaming = false,
  streamingContent = '',
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [streamingTimestamp] = useState(() => Date.now());

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingContent]);

  // Welcome message when no messages
  if (messages.length === 0 && !isStreaming) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg
              className="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            业务AI助手
          </h3>
          <p className="text-gray-500 mb-4">
            告诉我你想创建什么类型的页面，我会帮你生成内容结构
          </p>
          <div className="bg-purple-50 rounded-xl p-4 text-left">
            <p className="text-sm font-medium text-purple-700 mb-2">💡 你可以这样开始：</p>
            <ul className="text-sm text-gray-600 space-y-1.5">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                "创建一个Python编程入门课程页面"
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                "设计一个产品发布会宣传页"
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 bg-purple-400 rounded-full" />
                "制作一个公司团队介绍页面"
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      className="h-full overflow-y-auto p-4 space-y-6"
    >
      {messages.map((message, index) => (
        <ChatMessageItem
          key={message.id}
          message={message}
          isLast={index === messages.length - 1}
          isStreaming={isStreaming && index === messages.length - 1 && message.role === 'assistant'}
        />
      ))}
      
      {/* Streaming message */}
      {isStreaming && streamingContent && (
        <ChatMessageItem
          message={{
            id: 'streaming',
            role: 'assistant',
            content: streamingContent,
            timestamp: streamingTimestamp,
          }}
          isLast={true}
          isStreaming={true}
        />
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};
